/**
 * Entry-DAG operations (RFC 0215: Log substrate).
 *
 * Pure functions over a LogDoc value. No I/O. These run inside an Automerge
 * `handle.change(...)` at runtime and are the conformance surface Searugast
 * mirrors. `applyAppend` takes an explicit id and createdAt so behaviour is
 * deterministic (id/timestamp generation lives in the BranchLog wrapper).
 */

import type { Entry, EntryId, LogDoc, NewEntry, TimestampMs } from './types'

/** A fresh, empty per-branch log document. */
export function emptyLog(): LogDoc {
  return { schemaVersion: 1, entries: {}, toolWorkers: {} }
}

/** Append an entry. Mutates `doc` (an Automerge change draft) and returns the id. */
export function applyAppend(
  doc: LogDoc,
  id: EntryId,
  payload: NewEntry,
  parents: EntryId[],
  createdAt: TimestampMs,
): EntryId {
  doc.entries[id] = {
    id,
    parents: [...parents],
    createdAt,
    payload,
  }
  return id
}

export function get(doc: LogDoc, id: EntryId): Entry | undefined {
  return doc.entries[id]
}

/** Entries that no other entry lists as a parent: the current DAG heads. */
export function heads(doc: LogDoc): EntryId[] {
  const referenced = new Set<EntryId>()
  for (const e of Object.values(doc.entries)) {
    for (const p of e.parents) referenced.add(p)
  }
  return Object.keys(doc.entries).filter((id) => !referenced.has(id))
}

/** Direct children of `id` (entries that list `id` as a parent). */
export function children(doc: LogDoc, id: EntryId): Entry[] {
  return Object.values(doc.entries).filter((e) => e.parents.includes(id))
}

/** Direct parents of `id` that exist in the document. */
export function parents(doc: LogDoc, id: EntryId): Entry[] {
  const e = doc.entries[id]
  if (!e) return []
  return e.parents.map((p) => doc.entries[p]).filter((x): x is Entry => !!x)
}

/** All ancestors of `id` (transitive parents), excluding `id` itself. */
export function ancestors(doc: LogDoc, id: EntryId): Entry[] {
  const seen = new Set<EntryId>()
  const out: Entry[] = []
  const stack = [...(doc.entries[id]?.parents ?? [])]
  while (stack.length) {
    const cur = stack.pop()!
    if (seen.has(cur)) continue
    seen.add(cur)
    const e = doc.entries[cur]
    if (!e) continue
    out.push(e)
    stack.push(...e.parents)
  }
  return out
}

/** The selected entries and their complete causal past (RFC 0302 S2). */
export function ancestorClosure(
  doc: LogDoc,
  selected: readonly EntryId[],
): Set<EntryId> {
  const seen = new Set<EntryId>()
  const stack = [...selected]
  while (stack.length) {
    const id = stack.pop()!
    if (seen.has(id)) continue
    const entry = doc.entries[id]
    if (!entry) continue
    seen.add(id)
    stack.push(...entry.parents)
  }
  return seen
}

/**
 * Current heads of the causal branch rooted at `root`.
 *
 * A queued run may cease to be a global head while an older sibling is being
 * answered. Its provider context must still follow only entries descended from
 * that run; global heads would silently join the sibling (RFC 0302 S2).
 */
export function lineageHeads(doc: LogDoc, root: EntryId): EntryId[] {
  const lineage = new Set<EntryId>()
  for (const entry of topoEntries(doc)) {
    if (entry.id === root || entry.parents.some((p) => lineage.has(p))) {
      lineage.add(entry.id)
    }
  }
  const referenced = new Set<EntryId>()
  for (const id of lineage) {
    for (const parent of doc.entries[id]?.parents ?? []) {
      if (lineage.has(parent)) referenced.add(parent)
    }
  }
  return [...lineage].filter((id) => !referenced.has(id))
}

/**
 * Heads a CONTINUE may read (RFC 0302 S3).
 *
 * The global frontier, minus anything descended from a run this turn has no
 * business reading. A continue picks up where the conversation GOT TO — after a
 * serialized drain, the previous turn's completed head — and that is two
 * exclusions, not one:
 *
 *  - A BRANCH. "Nothing merges merely because it finished first" (3d). Reading
 *    the frontier flat would merge a parallel branch's work into the mainline
 *    silently, on timing.
 *  - A turn that has not ENDED. A post typed while this one was queued is on the
 *    log already, and answering it here would answer a question its own turn is
 *    about to answer again.
 *
 * `excluded` is those runs. The run's own lineage is never excluded, so a
 * branch's second turn still continues its own branch.
 */
export function continuationHeads(
  doc: LogDoc,
  runId: EntryId,
  excluded: readonly EntryId[],
): EntryId[] {
  const foreign = excluded.filter((root) => root !== runId)
  if (foreign.length === 0) return heads(doc)
  const kept = heads(doc).filter((head) => {
    const past = ancestorClosure(doc, [head])
    if (head === runId || past.has(runId)) return true
    return !foreign.some((root) => past.has(root))
  })
  // Never nothing: a turn with no heads at all would be sent an empty
  // transcript, which is a worse answer than a wider one.
  return kept.length > 0 ? kept : heads(doc)
}

function topoEntries(doc: LogDoc): Entry[] {
  // A parent is always written before its child by BranchLog. Sorting by the
  // stable document order is unnecessary here: repeated passes make this pure
  // helper tolerate imported documents whose object-key order differs.
  const remaining = new Set(Object.keys(doc.entries))
  const out: Entry[] = []
  while (remaining.size) {
    let progressed = false
    for (const id of remaining) {
      const entry = doc.entries[id]
      if (entry.parents.some((p) => remaining.has(p))) continue
      out.push(entry)
      remaining.delete(id)
      progressed = true
    }
    if (!progressed) break
  }
  return out
}
